import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  ArrowRightIcon,
  CheckIcon,
  MinusIcon,
  PlusIcon,
  Button,
  Chip,
  Heading,
  Tooltip,
  XIcon,
  cn,
  usePreloadIntent,
} from "@stacklet/ui";
import {
  graphql,
  useLazyLoadQuery,
  useQueryLoader,
  useRefetchableFragment,
} from "react-relay";
import { usePreloadedQuery } from "react-relay/hooks";
import { generatePath, useNavigate } from "react-router";

import { Avatar } from "app/components/avatar/Avatar";
import { ExportCsv } from "app/components/csv-export/ExportCsv";
import { useFilteredCsvExport } from "app/components/csv-export/useFilteredCsvExport";
import { HumanTimestamp } from "app/components/HumanTimestamp";
import { Link } from "app/components/links";
import { StackletGrid } from "app/components/table/StackletGrid";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { paths } from "app/constants/paths";
import { useUserContext } from "app/contexts/UserContext";
import {
  sortModelToOrder,
  useCursorPagination,
  type CursorVariables,
} from "app/hooks/grid";
import { useDebouncedCallback, useStickyPageSize } from "app/hooks/ui";
import { completeVariables } from "app/utils/routePreload";
import { preloadUserDetailsOnHover } from "app/views/settings/users/user-list/details/userDetailsPreload";

import {
  buildUserFilter,
  USER_BROWSER_PAGE_SIZE_OPTIONS,
  userBrowserVariables,
} from "./userBrowserVariables";

import type { UserBrowser_users$key } from "./__generated__/UserBrowser_users.graphql";
import type {
  UserBrowserQuery,
  UserBrowserQuery$variables,
} from "./__generated__/UserBrowserQuery.graphql";
import type { UserBrowserRefetchQuery } from "./__generated__/UserBrowserRefetchQuery.graphql";
import type { UserBrowserScope } from "./userBrowserVariables";
import type {
  GridColDef,
  GridEventListener,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import type { PreloadedRoute } from "app/utils/routePreload";
import type { PreloadedQuery } from "react-relay";

/** The table id a browser's column and page-size settings are kept under. */
const DEFAULT_TABLE_ID = "user-browser";
/** Default number of users per page */
const DEFAULT_PAGE_SIZE = 5;
/** Width of the add/remove column, sized to the icon button it holds */
const TRANSFER_COLUMN_WIDTH = 44;
/** Fields exempt from autosize; only `name` is left off, so it stretches. */
const FIXED_WIDTH_FIELDS = ["transfer", "hasApiKeys", "lastLogin", "actions"];

/**
 * Every field the browser can render, for {@link Props.fixedColumnWidths}.
 * Exempting a field that isn't currently rendered is harmless, so this needn't
 * track which optional columns are on. Stable identity.
 */
const ALL_FIELDS = ["transfer", "name", "hasApiKeys", "lastLogin", "actions"];

/** Columns included in the CSV export of the listed users */
const CSV_COLUMNS = [
  { name: "Name", path: "name" },
  { name: "Email", path: "email" },
  { name: "Last Login", path: "lastLogin" },
] as const;

// Match the design's grid chrome: space-xs cell padding, a short header row with
// small secondary-coloured labels, and no vertical padding fighting the fixed
// row height.
const GRID_CLASS_NAMES = [
  "[&_.MuiDataGrid-cell]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!text-text-secondary",
  "[&_.MuiDataGrid-columnHeaderTitle]:!text-label-small",
].join(" ");

// The scope types live with the query variables (see userBrowserVariables), but
// are part of this component's props, so callers can keep importing them here.
export type { GroupMembership, UserBrowserScope } from "./userBrowserVariables";

/** A user as handed to {@link Props.onTransfer} */
export interface TransferUser {
  /** Relay node id, for invalidating the user's cached record */
  id: string;
  /** Stable identifier, used to add/remove the user as a group member */
  uuid: string;
  /** Human label for alerts and accessible names */
  label: string;
}

const userBrowserQuery = graphql`
  query UserBrowserQuery($filterElement: FilterElementInput, $first: Int) {
    ...UserBrowser_users
      @arguments(filterElement: $filterElement, first: $first)
  }
`;

/** A single row in the user grid */
interface UserRow {
  id: string;
  uuid: string;
  key: number;
  name?: string | null;
  email?: string | null;
  lastLogin?: string | null;
  hasApiKeys: boolean;
}

interface UserDetailsPreloadAreaProps {
  /** The user whose details page to preload; nothing is preloaded without one. */
  userKey: number | undefined;
  /** Class names for the wrapping element. */
  className?: string;
  children: ReactNode;
}

/**
 * Preloads a user's details page once the pointer rests on what it wraps — the
 * name cell, which opens the page through the row's click rather than a link
 * of its own.
 */
function UserDetailsPreloadArea({
  userKey,
  className,
  children,
}: UserDetailsPreloadAreaProps) {
  const preloadHandlers = usePreloadIntent(
    userKey === undefined
      ? undefined
      : () => preloadUserDetailsOnHover(userKey),
  );

  return (
    <div className={className} {...preloadHandlers}>
      {children}
    </div>
  );
}

/** Props for the UserBrowser component */
interface Props {
  /** Which users to list; see {@link UserBrowserScope} */
  scope: UserBrowserScope;
  /** Heading above the grid; pass null to render none */
  title?: string | null;
  /**
   * Explanatory line under the heading. Defaults to none: what the list means
   * depends on how its scope is managed, which only the caller knows.
   */
  description?: string | null;
  /** Whether to offer a CSV export of the (filtered) list */
  enableExport?: boolean;
  /**
   * Whether to hold every column at its declared width instead of autosizing
   * from content (the name column still flexes to fill the remainder). Set
   * when several browsers render side by side — e.g. one per expanded row —
   * so their columns line up and don't visibly resize after each mounts.
   */
  fixedColumnWidths?: boolean;
  /**
   * Search term supplied by the parent. Providing it hands search over to the
   * caller: the grid's own toolbar (and its search field) is hidden and the
   * term is applied as given, so the caller owns any debouncing.
   */
  searchTerm?: string;
  /**
   * Seeds the browser's own search field at mount, unlike `searchTerm` which
   * hands control (and the field) to the parent. Lets a term survive when the
   * parent swaps this browser for another while keeping the field here.
   */
  initialSearchTerm?: string;
  /** Called with the applied term, so the parent can retain it across swaps. */
  onSearchTermChange?: (term: string) => void;
  /** Storage key for the grid's sticky column state; unique per rendered grid */
  tableId?: string;
  /**
   * Row action that moves the user in or out of the group. Supplying it adds a
   * leading column of `+` (for a non-member) or `−` (for a member) buttons.
   * Only meaningful for a group scope, whose membership can be edited.
   */
  onTransfer?: (user: TransferUser) => void;
  /** Disables the transfer buttons, e.g. while a membership change is in flight */
  isTransferDisabled?: boolean;
  /** Tooltip explaining why the transfer buttons are disabled */
  transferDisabledReason?: string;
  /**
   * Changing this value refetches the first page. Used to resync the list when
   * something outside it — a membership change made on the other list —
   * invalidates what it is showing.
   */
  refreshToken?: number;
  /** Content for the toolbar's right cluster, just before the Columns button. */
  toolbarActions?: ReactNode;
  /** Drops the search field's box so it blends into the toolbar. */
  transparentSearch?: boolean;
  /**
   * Rows per page until the user picks a size, which is then remembered under
   * `tableId` and takes precedence; defaults to 5.
   */
  defaultPageSize?: number;
  /** Renders the export as a compact icon-only button at the far right of the toolbar. */
  exportTrailing?: boolean;
  /** Draws vertical dividers between columns; defaults to on. */
  showColumnBorders?: boolean;
  /**
   * The query a route loader started fetching for this browser. Absent when
   * the browser is rendered without one, and null when the loader couldn't
   * preload — the browser then fetches on mount instead.
   */
  preloaded?: PreloadedRoute<UserBrowserQuery> | null;
}

/**
 * Browses one scope of the user directory: a searchable, sortable,
 * cursor-paginated grid showing an avatar, name and email, whether they hold
 * API keys and their last login, with a link through to each user's details
 * page and CSV export of the (filtered) list.
 *
 * The scope picks the slice: one side of a group's membership (the read-only
 * members list on the group details page, or half of a transfer list), or the
 * users an identity provider has provisioned (the browser on its details
 * page). By default the browser brings its own search field, heading and
 * export; `searchTerm` hands search to a parent driving several grids from one
 * field, and `onTransfer` adds the add/remove column of a transfer list. See
 * {@link GroupMembershipTransferList}.
 *
 * Column controls and the toolbar come from the shared XGridTable (MUI
 * DataGrid). Sorting is done by the server: the grid holds one page of a
 * larger list, so ordering that page alone would misrepresent the whole. Only
 * the name column sorts, as that is the only order the `users` connection
 * offers. Pagination replaces the page on each navigation (rather than
 * appending), so it uses `useRefetchableFragment` with explicit forward
 * (`first`/`after`) and backward (`last`/`before`) cursors instead of a
 * `@connection`-backed pagination fragment.
 */
export function UserBrowser({ preloaded, ...props }: Props) {
  const {
    scope,
    searchTerm,
    initialSearchTerm,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    tableId = DEFAULT_TABLE_ID,
  } = props;

  // Users per page, remembered under the grid's table id once picked; the
  // caller's default applies until then. Read here, where the query is loaded,
  // so the first page is the size the user last chose — and a route loader
  // preloading the browser reads the same choice (see readStickyPageSize).
  const [initialPageSize, setStoredPageSize] = useStickyPageSize(
    tableId,
    USER_BROWSER_PAGE_SIZE_OPTIONS,
    defaultPageSize,
  );

  // What this browser would ask for on mount. `initialSearchTerm` seeds the
  // field once; a controlled `searchTerm` wins.
  const variables = userBrowserVariables(
    scope,
    searchTerm ?? initialSearchTerm ?? "",
    initialPageSize,
  );

  // A preloaded reference is taken only if it is fetching exactly this, and
  // only for one mount: whoever mounted first released it on the way out, and a
  // released reference reads from records Relay is free to collect. Anything
  // else — no loader, a loader that couldn't preload, a browser mounting again
  // on the same loader data — loads on mount, as the browser always has.
  //
  // Decided once, on mount. A host that lifts the applied search term and
  // hands it back as `initialSearchTerm` (the users directory writes it to the
  // URL) changes the variables after the first render; deciding again then
  // would swap one branch for the other, unmounting the grid mid-search and
  // suspending on a fresh load while its refetch was already on the wire. A
  // reference released after this point is caught by `PreloadedUserBrowser`.
  //
  // That split is deliberate: unlike a page, the browser is a shared component
  // whose hosts may remount it while its first request is still in flight. A
  // browser that committed empty and only then loaded its query — the
  // `useRoutePreloadedQuery` shape the pages use — left the remounted copy
  // suspended on a request whose completion it never saw, re-rendering without
  // end (see the cloud organization details page's tests).
  const [usable] = useState(
    () =>
      preloaded != null &&
      !preloaded.queryRef.isDisposed &&
      preloaded.variablesKey ===
        JSON.stringify(
          completeVariables<UserBrowserQuery>(userBrowserQuery, variables),
        ),
  );

  const loadedProps: LoadedProps = {
    ...props,
    initialPageSize,
    onPageSizeChange: setStoredPageSize,
  };

  return usable && preloaded ? (
    <PreloadedUserBrowser
      {...loadedProps}
      fallbackVariables={variables}
      preloaded={preloaded}
    />
  ) : (
    <LazyUserBrowser {...loadedProps} initialVariables={variables} />
  );
}

/** The browser's props once `UserBrowser` has settled how it loads. */
interface LoadedProps extends Omit<Props, "preloaded"> {
  /** Rows on the first page: the remembered size, or the caller's default. */
  initialPageSize: number;
  /** Remembers a page size the user picks. */
  onPageSizeChange: (size: number) => void;
}

interface LazyProps extends LoadedProps {
  /** The variables to load with, as built by `UserBrowser`. */
  initialVariables: UserBrowserQuery$variables;
}

/** A browser nothing preloaded for: loads its query on mount. */
function LazyUserBrowser({ initialVariables, ...props }: LazyProps) {
  // Read once: every later change (search, refresh, paging) goes through
  // `refetch`, which keeps the loaded page mounted rather than re-suspending
  // the whole browser.
  const [variables] = useState(initialVariables);

  const queryData = useLazyLoadQuery<UserBrowserQuery>(
    userBrowserQuery,
    variables,
  );

  return <UserBrowserGrid {...props} queryData={queryData} />;
}

interface PreloadedProps extends LoadedProps {
  /** The query a route loader started fetching, checked by `UserBrowser`. */
  preloaded: PreloadedRoute<UserBrowserQuery>;
  /**
   * What the browser would ask for now, from its current props, should the
   * preloaded reference be released while it stays mounted.
   */
  fallbackVariables: UserBrowserQuery$variables;
}

/**
 * A browser rendering from the query its host's route loader started. The
 * reference is owned from here on: it is released when the browser unmounts,
 * along with the store records it retains.
 */
function PreloadedUserBrowser({
  preloaded,
  fallbackVariables,
  ...props
}: PreloadedProps) {
  const [queryRef] = useQueryLoader<UserBrowserQuery>(
    userBrowserQuery,
    preloaded.queryRef,
  );

  // useQueryLoader lets go of the reference if React hides and re-shows the
  // browser (its effects are torn down in between); the browser then loads
  // for itself, as it would have with nothing preloaded. It loads what its
  // props ask for now, not what the loader fetched: a host may have handed
  // back an applied search term since, and the grid it remounts seeds its
  // field from those same props, so the two must agree.
  if (!queryRef || queryRef.isDisposed) {
    return <LazyUserBrowser {...props} initialVariables={fallbackVariables} />;
  }

  return <PreloadedUserBrowserGrid {...props} queryRef={queryRef} />;
}

interface PreloadedGridProps extends LoadedProps {
  /** Reference to the loaded users query. */
  queryRef: PreloadedQuery<UserBrowserQuery>;
}

function PreloadedUserBrowserGrid({ queryRef, ...props }: PreloadedGridProps) {
  const queryData = usePreloadedQuery<UserBrowserQuery>(
    userBrowserQuery,
    queryRef,
  );

  return <UserBrowserGrid {...props} queryData={queryData} />;
}

interface GridProps extends LoadedProps {
  /** The loaded users query, for the fragment the grid reads. */
  queryData: UserBrowser_users$key;
}

function UserBrowserGrid({
  queryData,
  scope,
  title = "Users",
  description = null,
  enableExport = true,
  fixedColumnWidths = false,
  searchTerm,
  initialSearchTerm,
  onSearchTermChange,
  tableId = DEFAULT_TABLE_ID,
  onTransfer,
  isTransferDisabled = false,
  transferDisabledReason,
  refreshToken,
  toolbarActions,
  transparentSearch,
  initialPageSize,
  onPageSizeChange,
  exportTrailing,
  showColumnBorders = true,
}: GridProps) {
  const navigate = useNavigate();

  // With a `searchTerm` the parent owns the term (and its debounce); otherwise
  // the browser renders its own field and debounces internally.
  const isSearchControlled = searchTerm !== undefined;

  const { currentUser } = useUserContext();
  const currentUserEmail = currentUser?.email;

  // `search` is the live input value; `appliedSearchRef` holds the term the
  // currently loaded page (and its cursors) was fetched with. Pagination builds
  // its filter from the ref so a not-yet-debounced keystroke can't pair a new
  // filter with a cursor from the old result set — and reading a ref (rather
  // than state) keeps the navigation callbacks stable.
  const seededSearch = searchTerm ?? initialSearchTerm ?? "";
  const [search, setSearch] = useState(seededSearch);
  const appliedSearchRef = useRef(seededSearch);
  // `sortModel` is what the grid's headers show; `orderRef` holds the same
  // sort in the API's syntax for the navigation callbacks, which (as with the
  // search) read a ref so they stay stable.
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const orderRef = useRef("");
  // Filter by the applied search (not the live field) so the CSV matches the
  // rows on screen.
  const { exportInProgress, exportParams, handleExport, markComplete } =
    useFilteredCsvExport(() =>
      buildUserFilter(scope, appliedSearchRef.current),
    );

  // Bridges the search debounce's `cancel` (created after the pager below) to
  // `onBeforeNavigate`, which the pager needs before that hook runs.
  const cancelSearchRef = useRef<() => void>(() => {});

  const [data, refetch] = useRefetchableFragment<
    UserBrowserRefetchQuery,
    UserBrowser_users$key
  >(
    graphql`
      fragment UserBrowser_users on Query
      @argumentDefinitions(
        first: { type: "Int" }
        last: { type: "Int" }
        after: { type: "String" }
        before: { type: "String" }
        filterElement: { type: "FilterElementInput" }
        order: { type: "String" }
      )
      @refetchable(queryName: "UserBrowserRefetchQuery") {
        users(
          first: $first
          last: $last
          after: $after
          before: $before
          filterElement: $filterElement
          order: $order
        ) {
          edges {
            node {
              id
              uuid
              key
              name
              email
              lastLogin
              apiKeys {
                pageInfo {
                  total
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows: UserRow[] = (data.users.edges ?? [])
    .map((edge) => edge.node)
    .filter((node): node is NonNullable<typeof node> => Boolean(node))
    .map((node) => ({
      id: node.id,
      uuid: node.uuid,
      key: node.key,
      name: node.name,
      email: node.email,
      lastLogin: node.lastLogin,
      hasApiKeys: Boolean(node.apiKeys.pageInfo.total),
    }));

  const { hasNextPage, hasPreviousPage, total } = data.users.pageInfo;

  // Every refetch carries the active filter (built from the applied search) and
  // the applied sort, so paging never drops the current search or order.
  const onNavigate = useCallback(
    (variables: CursorVariables) =>
      refetch(
        {
          ...variables,
          filterElement: buildUserFilter(scope, appliedSearchRef.current),
          order: orderRef.current,
        },
        { fetchPolicy: "network-only" },
      ),
    [refetch, scope],
  );

  // Any explicit navigation supersedes a pending debounced search, so cancel it
  // before paging — otherwise its stale callback fires later and resets the
  // page/filter, replacing the navigation just made.
  const onBeforeNavigate = useCallback(() => cancelSearchRef.current(), []);

  const {
    pageSize,
    isPending,
    rangeStart,
    rangeEnd,
    handleNext,
    handlePrevious,
    handlePageSizeChange,
    goToFirstPage,
  } = useCursorPagination({
    pageInfo: data.users.pageInfo,
    rowCount: rows.length,
    defaultPageSize: initialPageSize,
    onNavigate,
    onBeforeNavigate,
    onPageSizeChange,
  });

  // Debounced search apply: commit the term and reset to the first page. The
  // ref lets `onBeforeNavigate` above cancel it despite being defined here.
  const applySearch = useDebouncedCallback((value: string) => {
    appliedSearchRef.current = value;
    onSearchTermChange?.(value);
    goToFirstPage();
  }, SEARCH_DEBOUNCE_MS);
  useEffect(() => {
    cancelSearchRef.current = applySearch.cancel;
  }, [applySearch.cancel]);

  // A new sort order invalidates the loaded page and its cursors (they belong
  // to the old ordering), so the list restarts from its first page. Unlike an
  // explicit page move, a sort refines the view the user is typing into, so a
  // search still inside its debounce is applied along with the order (which
  // restarts the page itself) rather than cancelled by the navigation.
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    orderRef.current = sortModelToOrder(model);
    if (!applySearch.flush()) {
      goToFirstPage();
    }
  };

  // A parent-driven search term or refresh token invalidates the loaded page,
  // so refetch it from the start. The ref (rather than the dependency list
  // alone) decides whether anything actually changed, so a fresh
  // `goToFirstPage` identity after a fetch can't retrigger the refetch that
  // fetch just satisfied.
  const lastRefetchedRef = useRef({ searchTerm, refreshToken });
  useEffect(() => {
    const last = lastRefetchedRef.current;
    if (last.searchTerm === searchTerm && last.refreshToken === refreshToken) {
      return;
    }
    lastRefetchedRef.current = { searchTerm, refreshToken };
    if (searchTerm !== undefined) {
      appliedSearchRef.current = searchTerm;
    }
    goToFirstPage();
  }, [searchTerm, refreshToken, goToFirstPage]);

  const isMemberList =
    scope.kind === "group" && scope.membership !== "non-members";

  // Memoized for a stable identity: DataGridPro re-applies each colDef's own
  // width whenever the `columns` prop reference changes, and XGridTable then
  // re-arms its content autosize — so a fresh array every render makes every
  // re-render visibly bounce the column widths.
  const columns: GridColDef<UserRow>[] = useMemo(() => {
    const cols: GridColDef<UserRow>[] = [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 240,
        renderCell: ({ row }) => (
          <UserDetailsPreloadArea
            className="flex h-full items-center gap-sm"
            // Only where the row opens the user: see isRowClickable.
            userKey={onTransfer ? undefined : row.key}
          >
            <Avatar
              email={row.email ?? ""}
              name={row.name ?? row.email ?? ""}
              size={28}
            />
            <div className="leading-tight min-w-0">
              <div className="flex items-center gap-xs">
                <span className="truncate text-label-medium text-text-primary">
                  {row.name ?? "--"}
                </span>
                {Boolean(currentUserEmail) && row.email === currentUserEmail ? (
                  <Chip color="blue">You</Chip>
                ) : null}
              </div>
              <div className="truncate text-label-small text-text-secondary">
                {row.email ?? "--"}
              </div>
            </div>
          </UserDetailsPreloadArea>
        ),
      },
      // The server orders users by name only, so the remaining data columns
      // don't offer a sort: sorting one page client-side would misrepresent
      // the list.
      {
        field: "hasApiKeys",
        headerName: "API Keys",
        width: 76,
        maxWidth: 76,
        sortable: false,
        align: "center",
        renderCell: ({ row }) =>
          row.hasApiKeys ? (
            <CheckIcon
              aria-label="Has API keys"
              className="size-4 shrink-0 text-text-positive"
            />
          ) : (
            <XIcon
              aria-label="No API keys"
              className="size-4 shrink-0 text-text-secondary"
            />
          ),
      },
      {
        field: "lastLogin",
        headerName: "Last Login",
        type: "dateTime",
        width: 150,
        maxWidth: 150,
        sortable: false,
        valueGetter: (params) => (params.value ? new Date(params.value) : null),
        renderCell: ({ row }) => <HumanTimestamp timestamp={row.lastLogin} />,
      },
      {
        field: "actions",
        headerName: "",
        width: 64,
        maxWidth: 64,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Link
            className="inline-flex text-text-secondary hover:text-text-primary"
            onPreload={() => preloadUserDetailsOnHover(row.key)}
            textDecoration="none"
            to={generatePath(paths.userDetails, { key: String(row.key) })}
          >
            <ArrowRightIcon
              aria-label={`View ${row.name ?? row.email ?? "user"}`}
              size={16}
            />
          </Link>
        ),
      },
    ];

    if (onTransfer) {
      cols.unshift({
        field: "transfer",
        headerName: "",
        // One icon button, the same on every row, so the column never needs to
        // be any wider than the button. min and max pin it against the grid
        // sizing it from content; FIXED_WIDTH_FIELDS keeps autosize off it too.
        width: TRANSFER_COLUMN_WIDTH,
        minWidth: TRANSFER_COLUMN_WIDTH,
        maxWidth: TRANSFER_COLUMN_WIDTH,
        sortable: false,
        resizable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        renderCell: ({ row }) => {
          const label = row.name ?? row.email ?? "user";
          return (
            <Tooltip
              title={isTransferDisabled ? (transferDisabledReason ?? "") : ""}
              asChild
            >
              <span className="inline-flex">
                <Button
                  IconLeft={
                    isMemberList ? (
                      <MinusIcon size={14} />
                    ) : (
                      <PlusIcon size={14} />
                    )
                  }
                  aria-label={
                    isMemberList
                      ? `Remove ${label} from the group`
                      : `Add ${label} to the group`
                  }
                  isDisabled={isTransferDisabled}
                  onPress={() =>
                    onTransfer({ id: row.id, uuid: row.uuid, label })
                  }
                  type="button"
                  variant="plain"
                />
              </span>
            </Tooltip>
          );
        },
      });
    }

    return cols;
  }, [
    currentUserEmail,
    onTransfer,
    isMemberList,
    isTransferDisabled,
    transferDisabledReason,
  ]);

  // Reflect the keystroke immediately; the apply is debounced.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    applySearch.run(value);
  };

  // The whole row opens the user, matching the trailing arrow the last column
  // already offers. A transfer list is excluded: its rows are there to be added
  // or removed, so navigating away mid-edit would be a trap.
  const isRowClickable = !onTransfer;

  const handleRowClick: GridEventListener<"rowClick"> = ({ row }, event) => {
    // A click that landed on the row's own control (the details link, a
    // tooltip trigger) is that control's to handle, not the row's.
    if ((event.target as HTMLElement).closest("a, button")) return;

    navigate(
      generatePath(paths.userDetails, { key: String((row as UserRow).key) }),
    );
  };

  const hasHeader = title !== null || description !== null;

  return (
    <div className="flex flex-col">
      {title === null ? null : (
        <div className="border-b border-overlay-medium pb-xs">
          <Heading
            className="text-label-medium-strong text-text-primary"
            level={3}
          >
            {title}
          </Heading>
        </div>
      )}
      {description === null ? null : (
        // With no title of its own, the browser sits under a caller-supplied
        // section header that already spaces the description off its rule.
        <p
          className={cn(
            "text-label-small text-text-secondary",
            title !== null ? "mt-sm" : undefined,
          )}
        >
          {description}
        </p>
      )}

      {enableExport ? (
        // The grid block below sits flush against whatever precedes it, so the
        // progress bar has to space itself off it. `empty:` drops the margin
        // again while no export is running, when ExportCsv renders nothing.
        <div className="mb-sm empty:mb-0">
          <ExportCsv
            columns={CSV_COLUMNS}
            exportInProgress={exportInProgress}
            field="users"
            markComplete={markComplete}
            nodeId={null}
            params={exportParams}
            rowCount={total ?? rows.length}
          />
        </div>
      ) : null}

      <div className={hasHeader ? "mt-md" : ""}>
        <StackletGrid
          className={GRID_CLASS_NAMES}
          columnHeaderHeight={36}
          columns={columns}
          defaultDensity="comfortable"
          exportInProgress={exportInProgress}
          exportTrailing={exportTrailing}
          fixedWidthFields={fixedColumnWidths ? ALL_FIELDS : FIXED_WIDTH_FIELDS}
          getRowClassName={() =>
            isRowClickable ? "XGridTable-clickableRow" : ""
          }
          getRowId={(row) => row.id}
          handleExport={enableExport ? handleExport : undefined}
          hasToolbar={!isSearchControlled}
          loading={isPending}
          onRowClick={isRowClickable ? handleRowClick : undefined}
          onSearchChange={isSearchControlled ? undefined : handleSearchChange}
          onSortModelChange={handleSortModelChange}
          pagination={
            // Show the pager whenever there are rows — even on a single page —
            // so its page-size selector stays reachable.
            rows.length === 0
              ? undefined
              : {
                  pageSize,
                  pageSizeOptions: USER_BROWSER_PAGE_SIZE_OPTIONS,
                  onPageSizeChange: handlePageSizeChange,
                  hasNextPage,
                  hasPreviousPage,
                  onNextPage: handleNext,
                  onPreviousPage: handlePrevious,
                  rangeStart,
                  rangeEnd,
                  total,
                  unit: "user",
                  isDisabled: isPending,
                }
          }
          rowHeight={52}
          rows={rows}
          search={search}
          searchAriaLabel="Search users"
          showCellVerticalBorder={showColumnBorders}
          showColumnVerticalBorder={showColumnBorders}
          sortModel={sortModel}
          sortingMode="server"
          tableId={tableId}
          toolbarActions={toolbarActions}
          transparentSearch={transparentSearch}
          hideDensityButton
        />
      </div>
    </div>
  );
}
